// src/components/TractiveEffortGraph.js
import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import '../styles.css';

function TractiveEffortGraph({ params = {}, sessionId, vehicleId, eventId, powerData = [] }) {
  const [user, loading, error] = useAuthState(auth);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [vehicleName, setVehicleName] = useState('Unnamed Vehicle');
  const [eventName, setEventName] = useState('Unnamed Event');
  const [sessionName, setSessionName] = useState('Unnamed Session');
  const [syncStatus, setSyncStatus] = useState('Loading...');
  const svgRef = useRef(null);

  // Fetch vehicle, event, and session names
  useEffect(() => {
    const loadData = async () => {
      if (!user || !vehicleId || !eventId) {
        console.warn('Missing user, vehicleId, or eventId for TractiveEffortGraph');
        setSyncStatus('Error: Missing data');
        return;
      }
      try {
        setSyncStatus('Loading...');
        let sessionData = null, vehicleData = null, eventData = null;
        // Fetch session (if sessionId exists)
        if (sessionId) {
          const sessionDoc = await getDoc(doc(db, `users/${user.uid}/sessions/${sessionId}`));
          sessionData = sessionDoc.exists() ? sessionDoc.data() : null;
        }
        // Fetch vehicle and event
        const vehicleDoc = await getDoc(doc(db, `users/${user.uid}/vehicles/${vehicleId}`));
        vehicleData = vehicleDoc.exists() ? vehicleDoc.data() : null;
        const eventDoc = await getDoc(doc(db, `users/${user.uid}/events/${eventId}`));
        eventData = eventDoc.exists() ? eventDoc.data() : null;

        setSessionName(sessionData?.sessionName || 'Unnamed Session');
        setVehicleName(vehicleData?.name || 'Unnamed Vehicle');
        setEventName(eventData?.name || 'Unnamed Event');
        setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
      } catch (err) {
        console.error('Error fetching data for TractiveEffortGraph:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };
    if (!loading && user) loadData();

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);
    return () => {
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
    };
  }, [user, loading, sessionId, vehicleId, eventId]);

  // Extract parameters with validation
  const driveType = params['Drive Type'] || 'RWD';
  const numGears = isNaN(parseInt(params['Number of Gears'])) || parseInt(params['Number of Gears']) <= 0 ? 6 : parseInt(params['Number of Gears']);
  const finalDrive = isNaN(parseFloat(params['Final Drive'])) || parseFloat(params['Final Drive']) <= 0 ? 4 : parseFloat(params['Final Drive']);
  const dropGear = isNaN(parseFloat(params['Drop Gear'])) || parseFloat(params['Drop Gear']) <= 0 ? 1 : parseFloat(params['Drop Gear']);
  const revLimit = isNaN(parseFloat(params['Rev limit'])) || parseFloat(params['Rev limit']) <= 0 ? 8000 : parseFloat(params['Rev limit']);

  // Helper function to get circumference with compatibility for old and new naming
  const getCircumference = (position) => {
    // Check new naming first: Circumference (Front/Rear)
    let value = params[`Circumference (${position})`];
    if (value !== undefined && !isNaN(parseFloat(value)) && parseFloat(value) > 0) return parseFloat(value);

    // Check old naming: Front/Rear Circumference
    value = params[`${position} Circumference`];
    if (value !== undefined && !isNaN(parseFloat(value)) && parseFloat(value) > 0) return parseFloat(value);

    // Default
    return 2000;
  };

  const frontCircumference = getCircumference('Front');
  const rearCircumference = getCircumference('Rear');
  const gearRatios = Array.from({ length: numGears }, (_, i) => {
    const ratio = parseFloat(params[`Gear Ratio ${i + 1}`]);
    return isNaN(ratio) || ratio <= 0 ? (5 - i * 0.5) : ratio;
  });

  // Calculate effective tire circumference
  const effectiveCircumference = driveType === 'AWD'
    ? (frontCircumference + rearCircumference) / 2
    : driveType === 'FWD'
    ? frontCircumference
    : rearCircumference;

  // Constants
  const MM_TO_M = 0.001; // mm to meters
  const M_TO_KM = 0.001; // meters to km
  const MIN_PER_HOUR = 60; // minutes per hour
  const SVG_WIDTH = 600;
  const SVG_HEIGHT = 500;
  const MARGIN = { top: 40, right: 40, bottom: 60, left: 80 };
  const FULL_SCREEN_MARGIN = { top: 30, right: 20, bottom: 60, left: 60 }; 
  const GRAPH_WIDTH = SVG_WIDTH - MARGIN.left - MARGIN.right;
  const GRAPH_HEIGHT = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
  const FULL_SCREEN_GRAPH_HEIGHT = SVG_HEIGHT - FULL_SCREEN_MARGIN.top - FULL_SCREEN_MARGIN.bottom;

  // Calculate speed for a given RPM and gear
  const calculateSpeed = (rpm, gearRatio) => {
    if (isNaN(rpm) || isNaN(gearRatio) || gearRatio === 0) return 0;
    const axleRpm = rpm / (gearRatio * finalDrive * dropGear);
    const speedMPerMin = axleRpm * effectiveCircumference * MM_TO_M;
    const speedKmPerHour = speedMPerMin * MIN_PER_HOUR * M_TO_KM;
    return isNaN(speedKmPerHour) ? 0 : speedKmPerHour;
  };

  // Calculate RPM at a given speed for a gear
  const calculateRpm = (speed, gearRatio) => {
    if (isNaN(speed) || isNaN(gearRatio) || gearRatio === 0 || effectiveCircumference === 0) return 0;
    const speedMPerMin = (speed / MIN_PER_HOUR) / M_TO_KM;
    const axleRpm = speedMPerMin / (effectiveCircumference * MM_TO_M);
    const rpm = axleRpm * gearRatio * finalDrive * dropGear;
    return isNaN(rpm) ? 0 : rpm;
  };

  // Interpolate torque at a given RPM
  const interpolateTorque = (rpm) => {
    if (!powerData || !Array.isArray(powerData) || powerData.length < 2) return 0;
    const sortedData = powerData
      .filter(d => d && d.rpm && d.torque && !isNaN(parseFloat(d.rpm)) && !isNaN(parseFloat(d.torque)))
      .map(d => ({ rpm: parseFloat(d.rpm), torque: parseFloat(d.torque) }))
      .sort((a, b) => a.rpm - b.rpm);
    if (sortedData.length < 2) return 0;
    if (rpm <= sortedData[0].rpm) return sortedData[0].torque;
    if (rpm >= sortedData[sortedData.length - 1].rpm) return sortedData[sortedData.length - 1].torque;
    for (let i = 0; i < sortedData.length - 1; i++) {
      if (rpm >= sortedData[i].rpm && rpm <= sortedData[i + 1].rpm) {
        const x0 = sortedData[i].rpm;
        const x1 = sortedData[i + 1].rpm;
        const y0 = sortedData[i].torque;
        const y1 = sortedData[i + 1].torque;
        return y0 + (y1 - y0) * (rpm - x0) / (x1 - x0);
      }
    }
    return 0;
  };

  // Calculate tractive effort data for each gear
  const validPowerData = powerData
    ? powerData
        .filter(d => d && d.rpm && d.torque && !isNaN(parseFloat(d.rpm)) && !isNaN(parseFloat(d.torque)))
        .map(d => ({ rpm: parseFloat(d.rpm), torque: parseFloat(d.torque) }))
        .sort((a, b) => a.rpm - b.rpm)
    : [];

  const gearData = gearRatios.map((ratio, index) => {
    if (isNaN(ratio) || ratio <= 0) {
      console.warn(`Invalid gear ratio for gear ${index + 1}: ${ratio}`);
      return { ratio: 0, points: [], maxSpeed: 0 };
    }
    const totalRatio = ratio * finalDrive * dropGear;
    const points = [];
    // Start at 0 RPM
    const startTorque = interpolateTorque(0);
    const startAxleTorque = startTorque * totalRatio;
    const startSpeed = calculateSpeed(0, ratio);
    if (!isNaN(startSpeed) && !isNaN(startAxleTorque)) {
      points.push({ rpm: 0, speed: startSpeed, axleTorque: startAxleTorque });
    }
    // Add points from validPowerData
    validPowerData.forEach(data => {
      if (data.rpm <= revLimit) {
        const torque = interpolateTorque(data.rpm);
        const axleTorque = torque * totalRatio;
        const speed = calculateSpeed(data.rpm, ratio);
        if (!isNaN(speed) && !isNaN(axleTorque)) {
          points.push({ rpm: data.rpm, speed, axleTorque });
        }
      }
    });
    // Ensure point at revLimit
    const endTorque = interpolateTorque(revLimit);
    const endAxleTorque = endTorque * totalRatio;
    const maxSpeed = calculateSpeed(revLimit, ratio);
    if (!isNaN(maxSpeed) && !isNaN(endAxleTorque) && !points.some(p => p.rpm === revLimit)) {
      points.push({ rpm: revLimit, speed: maxSpeed, axleTorque: endAxleTorque });
    }
    // Sort points by speed for correct plotting
    points.sort((a, b) => (isNaN(a.speed) || isNaN(b.speed) ? 0 : a.speed - b.speed));
    return { ratio, points, maxSpeed: isNaN(maxSpeed) ? 0 : maxSpeed };
  });

  // Determine maximum power and starting point for constant power curve
  let maxPower = parseFloat(params['Power']) || 0;
  let peakPowerRpm = 0;
  if (isNaN(maxPower) || maxPower <= 0) {
    const powerPoints = validPowerData.length
      ? validPowerData.map(d => ({
          rpm: d.rpm,
          torque: d.torque,
          power: (d.torque * d.rpm) / 5252
        }))
      : [{ rpm: 0, torque: 0, power: 0 }];
    const peak = powerPoints.reduce((max, d) => (d.power > max.power ? d : max), { power: 0 });
    maxPower = peak.power;
    peakPowerRpm = peak.rpm;
  } else {
    peakPowerRpm = validPowerData.length
      ? validPowerData.reduce((closest, d) => {
          const power = (d.torque * d.rpm) / 5252;
          const diff = Math.abs(power - maxPower);
          return diff < Math.abs(((closest.torque * closest.rpm) / 5252) - maxPower) ? d : closest;
        }, validPowerData[0]).rpm
      : 0;
  }

  // Calculate constant power curve points starting at 1st gear's peak power speed
  const constantPowerPoints = [];
  const firstGearRatio = gearRatios[0] || 5; // Use 1st gear ratio, default to 5 if invalid
  const startSpeed = firstGearRatio ? calculateSpeed(peakPowerRpm, firstGearRatio) : 0;
  const maxSpeed = gearData.length && gearData.some(g => g.maxSpeed > 0) ? Math.max(...gearData.map(g => g.maxSpeed)) : 100;
  const speedStep = (maxSpeed - startSpeed) / (numGears * 4); // More points for smoother curve
  const baseTorque = maxPower && peakPowerRpm ? (maxPower * 5252) / peakPowerRpm : 0;
  const baseSpeed = startSpeed;
  for (let speed = startSpeed; speed <= maxSpeed; speed += speedStep) {
    if (!maxPower || !baseSpeed || baseSpeed === 0) {
      continue; // Skip if invalid base values
    }
    // Torque halves as speed doubles: axleTorque = baseTorque * (baseSpeed / speed)
    const axleTorque = baseTorque * (baseSpeed / speed) * firstGearRatio * finalDrive * dropGear;
    if (!isNaN(speed) && !isNaN(axleTorque) && axleTorque >= 0) {
      constantPowerPoints.push({ speed, axleTorque });
    }
  }
  // Sort points by speed for correct plotting
  constantPowerPoints.sort((a, b) => a.speed - b.speed);

  // Calculate optimal shift points
  const shiftPoints = [];
  for (let i = 0; i < gearData.length - 1; i++) {
    const currentGear = gearData[i];
    const nextGear = gearData[i + 1];
    if (currentGear.ratio === 0 || nextGear.ratio === 0) continue;
    const currentTotalRatio = currentGear.ratio * finalDrive * dropGear;
    const nextTotalRatio = nextGear.ratio * finalDrive * dropGear;
    let shiftSpeed = null;
    let shiftRpm = null;
    // Iterate over speeds from current gear's minimum speed to its maximum speed
    const minSpeed = currentGear.points[0]?.speed || 0;
    const maxGearSpeed = calculateSpeed(revLimit, currentGear.ratio);
    const speedStep = (maxGearSpeed - minSpeed) / 100; // Fine granularity for accuracy
    for (let speed = minSpeed; speed <= maxGearSpeed; speed += speedStep) {
      const currentRpm = calculateRpm(speed, currentGear.ratio);
      if (currentRpm > revLimit) continue;
      if (currentRpm < 1000) continue;
      const nextRpm = calculateRpm(speed, nextGear.ratio);
      if (nextRpm > revLimit) continue; // Ensure next gear's RPM is valid
      const currentTorque = interpolateTorque(currentRpm) * currentTotalRatio;
      const nextTorque = interpolateTorque(nextRpm) * nextTotalRatio;
      if (!isNaN(currentTorque) && !isNaN(nextTorque) && currentTorque <= nextTorque) {
        shiftSpeed = speed;
        shiftRpm = currentRpm;
        break;
      }
    }
    // If no intersection found, use rev limit
    if (!shiftSpeed) {
      shiftRpm = revLimit;
      shiftSpeed = calculateSpeed(revLimit, currentGear.ratio);
    }
    if (!isNaN(shiftSpeed) && !isNaN(shiftRpm)) {
      shiftPoints.push({ gear: i + 1, rpm: shiftRpm, speed: shiftSpeed });
    }
  }

  // Scales
  const maxTorque = gearData.length && gearData.some(g => g.points.length > 0)
    ? Math.max(...gearData.map(g => g.points.length > 0 ? Math.max(...g.points.map(p => p.axleTorque), 1000) : 1000), 1000)
    : 1000;

  const xScale = (speed) => (isNaN(speed) ? 0 : (speed / maxSpeed) * GRAPH_WIDTH);
  const yScale = (torque, height = GRAPH_HEIGHT) => {
    return isNaN(torque) ? height : height - (torque / maxTorque) * height;
  };

  // Generate X-axis ticks
  const xTicks = Array.from({ length: 5 }, (_, i) => (maxSpeed / 4) * i);

  // Generate Y-axis ticks
  const yStep = Math.ceil(maxTorque / 5 / 1000) * 1000;
  const yTicks = [];
  for (let torque = 0; torque <= maxTorque; torque += yStep) {
    yTicks.push(torque);
  }

  // Generate SVG paths with Bezier curves
  const generatePath = (points, yScale) => {
    if (!points || points.length < 2 || points.some(p => isNaN(p.speed) || isNaN(p.axleTorque))) {
      console.warn('Invalid points for path:', points);
      return '';
    }
    const scaledPoints = points.map(p => ({
      x: xScale(p.speed),
      y: yScale(p.axleTorque)
    }));
    let pathD = `M ${scaledPoints[0].x.toFixed(2)},${scaledPoints[0].y.toFixed(2)}`;
    for (let i = 1; i < scaledPoints.length - 1; i++) {
      const c1 = scaledPoints[i - 1];
      const c2 = scaledPoints[i];
      const c3 = scaledPoints[i + 1];
      const c1c2Dist = Math.hypot(c2.x - c1.x, c2.y - c1.y);
      const c2c3Dist = Math.hypot(c3.x - c2.x, c3.y - c2.y);
      const radius = 20; // Same radius as gear curves
      const effectiveRadius = Math.min(radius, c1c2Dist / 2, c2c3Dist / 2);
      if (c1c2Dist === 0 || c2c3Dist === 0 || isNaN(c1c2Dist) || isNaN(c2c3Dist)) {
        // Use linear segment for zero or invalid distances
        pathD += ` L ${c2.x.toFixed(2)},${c2.y.toFixed(2)}`;
        continue;
      }
      const c1c2Ratio = (c1c2Dist - effectiveRadius) / c1c2Dist;
      const c1c2Curve = {
        x: (1 - c1c2Ratio) * c1.x + c1c2Ratio * c2.x,
        y: (1 - c1c2Ratio) * c1.y + c1c2Ratio * c2.y
      };
      const c2c3Ratio = effectiveRadius / c2c3Dist;
      const c2c3Curve = {
        x: (1 - c2c3Ratio) * c2.x + c2c3Ratio * c3.x,
        y: (1 - c2c3Ratio) * c2.y + c2c3Ratio * c3.y
      };
      pathD += ` L ${c1c2Curve.x.toFixed(2)},${c1c2Curve.y.toFixed(2)} Q ${c2.x.toFixed(2)},${c2.y.toFixed(2)} ${c2c3Curve.x.toFixed(2)},${c2c3Curve.y.toFixed(2)}`;
    }
    pathD += ` L ${scaledPoints[scaledPoints.length - 1].x.toFixed(2)},${scaledPoints[scaledPoints.length - 1].y.toFixed(2)}`;
    return pathD;
  };

  // Format gear ratios text
  const ordinalSuffix = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  const gearRatiosText = gearRatios.map((ratio, i) => `${ordinalSuffix(i + 1)}: ${ratio.toFixed(3)}`).join(' ');



  // Render graph
  const renderGraph = (isFullScreen = false) => {
    const margin = isFullScreen ? FULL_SCREEN_MARGIN : MARGIN;
    const graphHeight = isFullScreen ? FULL_SCREEN_GRAPH_HEIGHT : GRAPH_HEIGHT;
    const localYScale = (torque) => yScale(torque, graphHeight);

    // Generate paths with correct scale
    const paths = gearData.map((gear, index) => ({
      pathD: generatePath(gear.points, localYScale),
      index: index + 1
    }));
    const constantPowerPath = generatePath(constantPowerPoints, localYScale);

    // Adjust label positions to align with gear curve at shift point
    const adjustedShiftPoints = shiftPoints.map((sp, i) => {
      const currentGear = gearData[sp.gear - 1];
      const currentTotalRatio = currentGear.ratio * finalDrive * dropGear;
      const currentTorque = interpolateTorque(sp.rpm) * currentTotalRatio;
      let yPos = localYScale(currentTorque) - 10; // 10px above the gear curve
      // Ensure label stays above the graph
      yPos = Math.min(yPos, -10);
      return { ...sp, yPos };
    });

    return (
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Axes */}
        <line
          x1="0"
          y1={graphHeight}
          x2={GRAPH_WIDTH}
          y2={graphHeight}
          stroke="#333"
          strokeWidth="2"
        />
        <line
          x1="0"
          y1="0"
          x2="0"
          y2={graphHeight}
          stroke="#333"
          strokeWidth="2"
        />
        {/* Axis Labels */}
        <text x={GRAPH_WIDTH / 2} y={graphHeight + 40} textAnchor="middle" className="graph-label">
          Speed (km/h)
        </text>
        <text
          x={-graphHeight / 2}
          y={-65}
          textAnchor="middle"
          transform="rotate(-90)"
          className="graph-label"
        >
          Axle Torque (lb-ft)
        </text>
        {/* Grid Lines */}
        {xTicks.map((speed, i) => (
          <line
            key={`x-grid-${i}`}
            x1={xScale(speed)}
            y1="0"
            x2={xScale(speed)}
            y2={graphHeight}
            stroke="#ccc"
            strokeDasharray="2,2"
          />
        ))}
        {yTicks.map((torque, i) => (
          <line
            key={`y-grid-${i}`}
            x1="0"
            y1={localYScale(torque)}
            x2={GRAPH_WIDTH}
            y2={localYScale(torque)}
            stroke="#ccc"
            strokeDasharray="2,2"
          />
        ))}
        {/* Gear Lines */}
        {paths.map((path, i) => (
          path.pathD && (
            <path
              key={`gear-${path.index}`}
              d={path.pathD}
              fill="none"
              stroke={`hsl(${i * 360 / numGears}, 70%, 50%)`}
              strokeWidth="3"
              strokeLinecap="round"
            />
          )
        ))}
        {/* Constant Power Curve */}
        {constantPowerPath && (
          <path
            d={constantPowerPath}
            fill="none"
            stroke="#d3d3d3"
            strokeWidth="2"
            strokeDasharray="5,5"
            strokeLinecap="round"
          />
        )}
        {/* Optimal Shift Point Lines and Labels */}
        {adjustedShiftPoints.map((sp, i) => (
          <g key={`shift-${i}`}>
            <line
              x1={xScale(sp.speed)}
              y1="0"
              x2={xScale(sp.speed)}
              y2={graphHeight}
              stroke="#333"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
            <text
              x={xScale(sp.speed)}
              y={sp.yPos}
              textAnchor="middle"
              className="gear-label"
            >
              {Math.round(sp.rpm)}
            </text>
          </g>
        ))}
        {/* Axis Tick Labels */}
        {xTicks.map((speed, i) => (
          <text
            key={`x-tick-${i}`}
            x={xScale(speed)}
            y={graphHeight + 20}
            textAnchor="middle"
            className="tick-label"
          >
            {speed.toFixed(0)}
          </text>
        ))}
        {yTicks.map((torque, i) => (
          <text
            key={`y-tick-${i}`}
            x={-10}
            y={localYScale(torque) + 5}
            textAnchor="end"
            className="tick-label"
          >
            {torque.toFixed(0)}
          </text>
        ))}
      </g>
    );
  };

  return (
    <>
      <div className="tab-group">
        <div className="tab-group-title-container">
          <h3 className="tab-group-title">Tractive Effort</h3>
          <button onClick={() => setShowFullScreen(true)} className="graph-action-btn" title="Expand">
            <i className="material-icons">open_in_full</i>
          </button>
        </div>
        <div className="tractive-effort-graph-container">
          <svg
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
            className="tractive-effort-graph"
            ref={svgRef}
          >
            {renderGraph()}
          </svg>
        </div>
      </div>
      {showFullScreen && (
        <div className="overlay-card">
          <div className="card-container tractive-effort-calculator-fullscreen">
            <div className="page-title">
              <h2>Tractive Effort Graph</h2>
              <div>
                <button onClick={() => setShowFullScreen(false)} className="close-button">✕</button>
              </div>
            </div>
            <div className="tractive-effort-info">
              <p><strong>Vehicle:</strong> {vehicleName}</p>
              <p><strong>Event:</strong> {eventName}</p>
              <p><strong>Session:</strong> {sessionName}</p>
              <p><strong>Gear Ratios:</strong> Final: {finalDrive.toFixed(3)}{dropGear !== 1 ? ` Drop: ${dropGear.toFixed(3)}` : ''} {gearRatiosText}</p>
              <p><strong>Tire Circumference:</strong> {driveType === 'AWD' ? `Front: ${frontCircumference} mm, Rear: ${rearCircumference} mm (Avg: ${effectiveCircumference.toFixed(0)} mm)` : `${driveType === 'FWD' ? 'Front' : 'Rear'}: ${effectiveCircumference} mm`}</p>
              <p><strong>Max Power:</strong> {maxPower.toFixed(1)} HP</p>
            </div>
            <div className="tractive-effort-graph-container">
              <svg
                viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                preserveAspectRatio="xMidYMid meet"
                className="tractive-effort-graph"
                ref={svgRef}
              >
                {renderGraph(true)}
              </svg>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TractiveEffortGraph;